import { useEffect, useState } from "react";
import { doc, onSnapshot, getDoc, DocumentSnapshot } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { useAuthState } from "@/auth/AuthProvider";

export type SubStatus = "loading" | "none" | "active" | "past_due" | "canceled" | "trialing" | "trial_expired";

const DEBUG = import.meta.env.VITE_DEBUG_LOGIN === "true";

/**
 * Resolves trial status from the top-level Firestore fields for users who have
 * no Stripe subscription (subscription.status is absent). Performs date math
 * against trialEndsAt so expired trials are not treated as active.
 */
function resolveTrialStatus(snap: DocumentSnapshot): SubStatus {
  const topLevel = snap.get("subscriptionStatus") as SubStatus | undefined;
  if (topLevel === 'trialing') {
    const trialEndsAt = snap.get("trialEndsAt");
    const endsAtMs: number = typeof trialEndsAt?.toMillis === 'function'
      ? trialEndsAt.toMillis()
      : Number(trialEndsAt ?? 0);
    return Date.now() > endsAtMs ? 'trial_expired' : 'trialing';
  }
  // No trial fields present (legacy user or post-subscription) — fail open
  return topLevel ?? 'active';
}

export function useSubscription() {
  const { user, status } = useAuthState();
  const [sub, setSub] = useState<SubStatus>("loading");

  useEffect(() => {
    const uid = user?.uid ?? null;
    
    if (status !== "signedIn" || !uid) {
      setSub("loading");
      return;
    }

    console.log("[useSubscription] 🔵 Starting for uid:", uid.slice(0, 8));

    // FIX CLASS A: Use getDoc() first for immediate, non-blocking check
    // Then attach onSnapshot for real-time updates AFTER initial state resolved
    let listenerAttached = false;
    
    getDoc(doc(db, "users", uid))
      .then((snap) => {
        console.log("[useSubscription] 📄 Document exists:", snap.exists());
        const s = snap.get("subscription.status") as SubStatus | undefined;
        // When no Stripe subscription exists, resolve trial status with date math
        // instead of failing open. Trial users only have the top-level subscriptionStatus field.
        const initialStatus = s !== undefined ? s : resolveTrialStatus(snap);
        console.log("[useSubscription] ✅ Initial status:", initialStatus, "(raw:", s, ")");
        setSub(initialStatus);
        listenerAttached = true;
      })
      .catch((error) => {
        console.error("[useSubscription] getDoc failed, allowing navigation:", error);
        setSub("active"); // Fail open: allow navigation when Firestore is unavailable
        listenerAttached = true;
      });

    // Attach real-time listener for subscription status changes
    const unsubscribe = onSnapshot(
      doc(db, "users", uid),
      (snap) => {
        if (!listenerAttached) return; // Ignore snapshots until initial getDoc completes
        const s = snap.get("subscription.status") as SubStatus | undefined;
        const resolved = s !== undefined ? s : resolveTrialStatus(snap);
        setSub(resolved);
        if (DEBUG) console.log("[useSubscription] Listener update:", resolved);
      },
      (error) => {
        console.error("[useSubscription] Listener error (degraded mode):", error);
        if (!listenerAttached) setSub("active"); // Fallback if listener fails before getDoc
      }
    );

    return unsubscribe;
  }, [status, user?.uid]); // Use user.uid instead of user object to avoid infinite loops

  return { sub };
}
